import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Leaf, Menu, Package, Search, ShoppingBag, User, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { getCategories, getSettings, inr } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import CartDrawer from "@/components/CartDrawer";

const NAV_CATS = [
  { label: "Shop All", cat: "", testid: "nav-cat-all" },
  { label: "Eco Series", cat: "Eco Series", testid: "nav-cat-eco" },
  { label: "School & College", cat: "School & College", testid: "nav-cat-school" },
  { label: "Creative", cat: "Creative", testid: "nav-cat-creative" },
  { label: "Journals & Gifting", cat: "Journals & Gifting", testid: "nav-cat-journals" },
  { label: "Office & Filing", cat: "Office & Filing", testid: "nav-cat-office" },
];

const LINKS = [
  { label: "Bulk Orders", href: "#trade", testid: "nav-trade-link" },
  { label: "Track Order", href: "/track", testid: "nav-track-link" },
  { label: "Contact", href: "#contact", testid: "nav-contact-link" },
];

export const Header = () => {
  const { count } = useCart();
  const [cartOpen, setCartOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [freeShipAbove, setFreeShipAbove] = useState(499);
  const [navCats, setNavCats] = useState(NAV_CATS);
  const [announcement, setAnnouncement] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    getSettings()
      .then((s) => {
        setFreeShipAbove(s.free_shipping_above);
        setAnnouncement(s.announcement_text || "");
      })
      .catch(() => {});
    getCategories()
      .then((list) => {
        const visible = list.filter((c) => !c.hidden && (c.product_count || 0) > 0);
        if (visible.length > 0) {
          setNavCats([
            NAV_CATS[0],
            ...visible.map((c) => ({ label: c.name, cat: c.name, testid: `nav-cat-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` })),
          ]);
        }
      })
      .catch(() => {});
  }, []);

  const go = (path) => {
    setMenuOpen(false);
    navigate(path);
    setTimeout(() => {
      const hash = path.includes("#") ? path.split("#")[1] : null;
      if (hash) document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      else window.scrollTo({ top: 0, behavior: "smooth" });
    }, 80);
  };

  const search = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    go(`/?q=${encodeURIComponent(query.trim())}#shop`);
  };

  return (
    <>
      <div data-testid="announcement-bar" className="bg-primary text-primary-foreground text-[11px] font-semibold tracking-wide text-center py-2 px-4">
        {(announcement || `Free shipping on orders above ₹${freeShipAbove.toLocaleString("en-IN")} · Bulk & school orders welcome —`).replace("{free_shipping}", freeShipAbove.toLocaleString("en-IN"))}{" "}
        <button onClick={() => go("/#trade")} data-testid="announcement-bulk-link" className="underline underline-offset-2 hover:opacity-80 transition">
          request a quote
        </button>
      </div>

      <header className="sticky top-0 z-50 bg-[#F5F4F0]/85 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 h-16 md:h-20 flex items-center gap-4">
          <button onClick={() => go("/#top")} data-testid="header-logo" className="flex items-center shrink-0 transition-transform duration-300 hover:scale-[1.03]">
            <img src="/classina-logo.png" alt="Classina Products" className="h-9 md:h-11 w-auto" />
          </button>

          <form onSubmit={search} className="hidden md:flex flex-1 max-w-xl mx-auto items-center rounded-full border border-border bg-white/70 focus-within:ring-2 focus-within:ring-primary transition overflow-hidden">
            <input
              data-testid="header-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search notebooks, sketch pads, box files…"
              className="flex-1 bg-transparent px-5 py-2.5 text-sm outline-none placeholder:text-muted-foreground"
            />
            <button data-testid="header-search-submit" type="submit" className="px-5 py-2.5 text-muted-foreground hover:text-primary transition-colors" aria-label="Search">
              <Search className="w-4 h-4" />
            </button>
          </form>

          <div className="flex items-center gap-2 ml-auto md:ml-0">
            <button
              onClick={() => go("/track")}
              data-testid="header-track-button"
              className="hidden sm:flex p-2.5 rounded-full hover:bg-secondary transition-colors"
              aria-label="Track order"
              title="Track your order"
            >
              <Package className="w-5 h-5" />
            </button>
            <button
              onClick={() => go("/account")}
              data-testid="header-account-button"
              className="hidden sm:flex p-2.5 rounded-full hover:bg-secondary transition-colors"
              aria-label="My account"
              title="My account"
            >
              <User className="w-5 h-5" />
            </button>
            <button
              data-testid="header-cart-button"
              onClick={() => setCartOpen(true)}
              className="relative flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-lg"
            >
              <ShoppingBag className="w-4 h-4" />
              <span className="hidden sm:inline">Cart</span>
              {count > 0 && (
                <span data-testid="header-cart-count" className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-accent text-accent-foreground text-[10px] font-bold flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>

            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-button" className="md:hidden p-2 rounded-full hover:bg-secondary transition-colors">
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-background">
                <SheetHeader>
                  <SheetTitle className="font-serif text-2xl">Classina<sup className="text-xs">®</sup></SheetTitle>
                </SheetHeader>
                <form onSubmit={search} className="mt-6 flex items-center rounded-full border border-border bg-white/70 overflow-hidden">
                  <input
                    data-testid="mobile-search-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search products…"
                    className="flex-1 bg-transparent px-4 py-2.5 text-sm outline-none"
                  />
                  <button type="submit" data-testid="mobile-search-submit" className="px-4 text-muted-foreground" aria-label="Search">
                    <Search className="w-4 h-4" />
                  </button>
                </form>
                <nav className="flex flex-col gap-5 mt-8">
                  {[...navCats, ...LINKS].map((n) => (
                    <button
                      key={n.testid}
                      data-testid={`mobile-${n.testid}`}
                      onClick={() => go(n.cat !== undefined ? (n.cat ? `/?cat=${encodeURIComponent(n.cat)}#shop` : "/#shop") : n.href)}
                      className="text-left font-serif text-2xl font-medium hover:text-primary transition-colors"
                    >
                      {n.label}
                    </button>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <nav className="hidden md:block border-t border-border/40">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 flex items-center gap-8 h-11 overflow-x-auto">
            {[...navCats].map((c) => (
              <button
                key={c.testid}
                data-testid={c.testid}
                onClick={() => go(c.cat ? `/?cat=${encodeURIComponent(c.cat)}#shop` : "/#shop")}
                className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground hover:text-primary transition-colors whitespace-nowrap"
              >
                {c.label}
              </button>
            ))}
            <span className="w-px h-4 bg-border shrink-0" />
            {LINKS.map((l) => (
              <button
                key={l.testid}
                data-testid={l.testid}
                onClick={() => go(l.href)}
                className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground hover:text-primary transition-colors whitespace-nowrap"
              >
                {l.label}
              </button>
            ))}
          </div>
        </nav>
      </header>

      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
    </>
  );
};
